/**
 * RealtorLandingPage — public /realtors page.
 *
 * Education-only landing for realtors and buyer's agents: the pitch is
 * "send your buyer to LoanKnow for a readiness check and be the hero."
 * Deliberately no signup form — realtors don't need an account to refer.
 * The only CTA is sharing the /readiness link with a buyer.
 *
 * Future (see /referral partner portal): personal referral codes and a
 * top-agent leaderboard. Recognition only — LoanKnow never pays for
 * referrals, so there's nothing for an agent to disclose.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  CheckCircle, Sparkles, Shield, Copy, Check, Link as LinkIcon,
  AlertCircle, X, Users, Clock, Heart, Trophy, MessageSquare, TrendingUp,
  ChevronDown, ChevronUp, EyeOff, Award,
} from 'lucide-react';
import BorrowerHeader from '../components/layout/BorrowerHeader';
import ComplianceFooter from '../components/common/ComplianceFooter';

// Palette aligned with the Owen readiness experience (warm off-whites + indigo).
const COLORS = {
  ink: '#18181b',
  ink2: '#3f3f46',
  ink3: '#71717a',
  ink4: '#a1a1aa',
  rule: '#eceae5',
  bg: '#faf9f7',
  brand: '#4f46e5',
  brandSoft: '#eef2ff',
  brandBorder: '#e0e7ff',
  brandDark: '#312e81',
  good: '#059669',
  goodSoft: '#ecfdf5',
  bad: '#e11d48',
  badSoft: '#fff1f2',
};

const READINESS_URL = 'https://loanknow.com/readiness';

const SHARE_MESSAGE =
  'Before we look at more homes, try this — it\'s a free 3-minute readiness check ' +
  '(no credit pull, no sales calls, and nobody gets your info). You\'ll see exactly ' +
  'what price range fits and which loan programs work for you: ' + READINESS_URL;

/** Copy-to-clipboard button with a transient "Copied" state. */
const CopyButton = ({ text, label, copiedLabel = 'Copied!', style = {} }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API can be unavailable (http, permissions) — fall back.
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        fontSize: 15, fontWeight: 600, cursor: 'pointer',
        borderRadius: 12, border: 'none',
        padding: '16px 32px',
        backgroundColor: copied ? COLORS.good : COLORS.brand,
        color: 'white',
        boxShadow: '0 8px 24px -8px rgba(79,70,229,0.45)',
        transition: 'background 0.2s ease',
        ...style,
      }}
    >
      {copied ? <Check size={17} /> : <Copy size={17} />}
      {copied ? copiedLabel : label}
    </button>
  );
};

const RealtorLandingPage = () => {
  const navigate = useNavigate();
  return (
    <div style={{ backgroundColor: 'white', color: COLORS.ink, minHeight: '100vh' }}>
      <Hero />
      <Problem />
      <ValueProps />
      <HowItWorks />
      <ShareKit />
      <ComingSoon />
      <FAQ />

      <div style={{ padding: '32px 16px 48px', textAlign: 'center', color: COLORS.ink3, fontSize: 13 }}>
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          style={{ color: COLORS.ink3, textDecoration: 'none' }}
        >
          ← Back to LoanKnow home
        </a>
      </div>

      <ComplianceFooter />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────
const Hero = () => (
  <section style={{
    position: 'relative',
    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #3730a3 100%)',
    color: 'white', overflow: 'hidden',
  }}>
    <BorrowerHeader variant="transparent" showSignIn={false} />

    <div aria-hidden style={{
      position: 'absolute', top: -120, right: -120, width: 380, height: 380, borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(99,102,241,0.55) 0%, transparent 70%)', filter: 'blur(20px)',
    }} />
    <div aria-hidden style={{
      position: 'absolute', bottom: -100, left: -100, width: 320, height: 320, borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(165,180,252,0.3) 0%, transparent 70%)', filter: 'blur(20px)',
    }} />

    <div style={{
      maxWidth: 1100, margin: '0 auto', padding: '80px 24px 120px',
      position: 'relative', zIndex: 1,
    }}>
      <div style={{ maxWidth: 820, margin: '0 auto', textAlign: 'center' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '6px 14px', borderRadius: 999,
          backgroundColor: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.2)',
          fontSize: 13, fontWeight: 500, color: '#a5b4fc',
          marginBottom: 28,
        }}>
          <Sparkles size={14} /> For Realtors & Buyer's Agents
        </div>

        <h1 style={{
          fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
          fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.025em',
          margin: 0, marginBottom: 24, color: 'white',
        }}>
          Send buyers in ready,<br />
          <span style={{
            background: 'linear-gradient(90deg, #a5b4fc 0%, #c4b5fd 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            not just excited.
          </span>
        </h1>

        <p style={{
          fontSize: 'clamp(1.05rem, 2vw, 1.25rem)',
          lineHeight: 1.55, opacity: 0.9,
          maxWidth: 700, margin: '0 auto 40px',
        }}>
          LoanKnow gives your buyer a free, 3-minute mortgage readiness check — no credit pull,
          no sales calls, completely anonymous. They come back knowing their real budget and
          which loan programs fit. You look like the agent who has it all figured out.
        </p>

        <div style={{
          display: 'flex', gap: 14, justifyContent: 'center',
          flexWrap: 'wrap', marginBottom: 32,
        }}>
          <CopyButton
            text={READINESS_URL}
            label="Copy the link to share"
            style={{ backgroundColor: 'white', color: COLORS.brandDark, boxShadow: '0 8px 30px rgba(255,255,255,0.18)' }}
          />
          <button
            onClick={() => document.getElementById('how-it-works-realtor')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              backgroundColor: 'transparent', color: 'white',
              padding: '16px 28px', fontSize: 15, fontWeight: 600,
              borderRadius: 12, border: '1px solid rgba(255,255,255,0.4)',
              cursor: 'pointer',
            }}
          >
            See how it works
          </button>
        </div>

        <div style={{
          display: 'flex', gap: 28, justifyContent: 'center', flexWrap: 'wrap',
          opacity: 0.7, fontSize: 13,
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={14} color="#86efac" /> Free for your buyer
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={14} color="#86efac" /> No account needed for you
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <CheckCircle size={14} color="#86efac" /> Your client stays yours
          </span>
        </div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// Problem
// ─────────────────────────────────────────────────────────────
const Problem = () => {
  const pains = [
    {
      icon: <AlertCircle size={22} />,
      title: 'Pre-qual letters that mean nothing',
      body: 'A 10-minute phone pre-qual tells you almost nothing about whether the deal survives underwriting. You find out the hard way, three weeks in.',
    },
    {
      icon: <X size={22} />,
      title: 'Weekends spent outside their budget',
      body: 'Buyers fall in love with homes they can\'t finance. Resetting expectations after the fact costs you the trust you\'ve built.',
    },
    {
      icon: <Users size={22} />,
      title: 'Recommending a lender feels risky',
      body: 'Point them to the wrong loan officer and it reflects on you. Point them to a lead site and they get 40 phone calls by dinner.',
    },
    {
      icon: <Clock size={22} />,
      title: 'Financing kills deals late',
      body: 'A surprise condition two weeks before close doesn\'t just cost the buyer the house — it costs you the commission and the referral.',
    },
  ];
  return (
    <section style={{ padding: '96px 24px', backgroundColor: 'white' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <SectionEyebrow>The financing gap is your problem too</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, color: COLORS.ink,
          textAlign: 'center', maxWidth: 740, margin: '8px auto 18px',
        }}>
          You do everything right, and the mortgage still blows up the deal.
        </h2>
        <p style={{
          fontSize: 17, color: COLORS.ink2, lineHeight: 1.6,
          textAlign: 'center', maxWidth: 660, margin: '0 auto 56px',
        }}>
          The best agents already vet their buyers' financing readiness. LoanKnow just makes
          that vetting instant, free, and safe to recommend.
        </p>
        <div style={{
          display: 'grid', gap: 20,
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        }}>
          {pains.map((p) => (
            <div key={p.title} style={{
              padding: 24, borderRadius: 12, backgroundColor: COLORS.bg,
              border: `1px solid ${COLORS.rule}`,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                backgroundColor: COLORS.badSoft, color: COLORS.bad,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 14,
              }}>{p.icon}</div>
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: COLORS.ink }}>{p.title}</h3>
              <p style={{ fontSize: 14, color: COLORS.ink2, lineHeight: 1.55, margin: 0 }}>{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Value props
// ─────────────────────────────────────────────────────────────
const ValueProps = () => {
  const benefits = [
    {
      icon: <TrendingUp size={26} />,
      title: 'Buyers who know their real budget',
      body: 'Owen, our AI guide, walks your buyer through about a dozen plain-English questions. They get a live Readiness Score, their true price range, and the loan programs that actually fit — before you book the next showing.',
    },
    {
      icon: <EyeOff size={26} />,
      title: 'Anonymous — never turned into a lead',
      body: 'This is the part your buyer will thank you for: no credit pull, no phone number required, and their information is never sold. To lenders they\'re "Borrower #48213" until they choose to reveal themselves. Nobody calls them at dinner.',
    },
    {
      icon: <Heart size={26} />,
      title: 'You stay the hero — and the agent',
      body: 'We\'re not a brokerage and we don\'t have agents. Your buyer comes back to you, better prepared, telling their friends you\'re the one who sent them. The relationship stays yours.',
    },
    {
      icon: <Shield size={26} />,
      title: 'Nothing to disclose, ever',
      body: 'LoanKnow is free for buyers and we don\'t pay for referrals — so there\'s no RESPA gray area, no kickback question, no disclosure to worry about. It\'s just a genuinely useful thing you shared.',
    },
    {
      icon: <Clock size={26} />,
      title: 'A 3-minute ask, not a homework assignment',
      body: 'It\'s a friendly chat, not a loan application. No documents, no account, no password. The easiest "do this before Saturday" you\'ll ever send.',
    },
    {
      icon: <Award size={26} />,
      title: 'Fewer fall-throughs, faster closes',
      body: 'Financing surprises get surfaced up front, not two weeks before close. And when your buyer is ready, vetted lenders compete for their file — so the mortgage keeps pace with your deal instead of stalling it.',
    },
  ];

  return (
    <section style={{ padding: '96px 24px', backgroundColor: COLORS.bg }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <SectionEyebrow color={COLORS.brand}>Why agents send buyers here</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, color: COLORS.ink,
          textAlign: 'center', maxWidth: 720, margin: '8px auto 18px',
        }}>
          The safest financing recommendation you can make.
        </h2>
        <p style={{
          fontSize: 17, color: COLORS.ink2, lineHeight: 1.6,
          textAlign: 'center', maxWidth: 640, margin: '0 auto 56px',
        }}>
          No lender to vouch for. No lead site that spams your client.
          Just an anonymous readiness check that makes your buyer stronger.
        </p>

        <div style={{
          display: 'grid', gap: 20,
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        }}>
          {benefits.map((b) => (
            <div key={b.title} style={{
              padding: 28, borderRadius: 14, backgroundColor: 'white',
              border: `1px solid ${COLORS.rule}`,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                backgroundColor: COLORS.brandSoft, color: COLORS.brand,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 18,
              }}>{b.icon}</div>
              <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: COLORS.ink }}>{b.title}</h3>
              <p style={{ fontSize: 14, color: COLORS.ink2, lineHeight: 1.6, margin: 0 }}>{b.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// How it works
// ─────────────────────────────────────────────────────────────
const HowItWorks = () => {
  const steps = [
    {
      num: '01',
      title: 'Share the link',
      body: 'Text or email loanknow.com/readiness to your buyer — there\'s a ready-made message below. No signup for you, no signup for them.',
    },
    {
      num: '02',
      title: 'They chat with Owen for ~3 minutes',
      body: 'About a dozen plain-English questions. Their Readiness Score builds live, and at the end they see their price range and the loan programs that fit — plus exactly what to improve if they\'re not ready yet.',
    },
    {
      num: '03',
      title: 'They come back to you prepared',
      body: 'Now you\'re showing homes inside a real budget to a buyer who understands their financing. When they\'re ready to transact, vetted lenders compete for their file anonymously — and your deal doesn\'t stall in underwriting.',
    },
  ];

  return (
    <section id="how-it-works-realtor" style={{ padding: '96px 24px', backgroundColor: 'white' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <SectionEyebrow>Three steps, zero paperwork</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, color: COLORS.ink,
          textAlign: 'center', maxWidth: 700, margin: '8px auto 56px',
        }}>
          From "just looking" to house-ready.
        </h2>

        <div style={{ display: 'grid', gap: 20 }}>
          {steps.map((s) => (
            <div key={s.num} style={{
              display: 'grid', gridTemplateColumns: '90px 1fr',
              gap: 24, alignItems: 'flex-start',
              padding: 28, borderRadius: 14,
              backgroundColor: COLORS.bg,
              border: `1px solid ${COLORS.rule}`,
            }}>
              <div style={{
                fontSize: 38, fontWeight: 800, lineHeight: 1,
                color: COLORS.brand, fontVariantNumeric: 'tabular-nums',
              }}>
                {s.num}
              </div>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: COLORS.ink }}>
                  {s.title}
                </h3>
                <p style={{ fontSize: 15, color: COLORS.ink2, lineHeight: 1.6, margin: 0 }}>
                  {s.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Share kit — the page's real CTA
// ─────────────────────────────────────────────────────────────
const ShareKit = () => (
  <section style={{ padding: '96px 24px', backgroundColor: COLORS.bg }}>
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <SectionEyebrow color={COLORS.brand}>Ready to use</SectionEyebrow>
      <h2 style={{
        fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
        lineHeight: 1.15, color: COLORS.ink,
        textAlign: 'center', maxWidth: 600, margin: '8px auto 18px',
      }}>
        Send it to your next buyer.
      </h2>
      <p style={{
        fontSize: 16, color: COLORS.ink2, lineHeight: 1.6,
        textAlign: 'center', maxWidth: 540, margin: '0 auto 40px',
      }}>
        Copy the link, or grab the ready-made message below and drop it into a text or email.
      </p>

      {/* The link */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        backgroundColor: 'white', border: `1px solid ${COLORS.rule}`,
        borderRadius: 14, padding: '16px 20px', marginBottom: 20,
      }}>
        <LinkIcon size={18} color={COLORS.brand} style={{ flexShrink: 0 }} />
        <span style={{
          flex: 1, minWidth: 200, fontSize: 15, fontWeight: 600, color: COLORS.ink,
          fontFamily: 'Consolas, monospace', wordBreak: 'break-all',
        }}>
          {READINESS_URL}
        </span>
        <CopyButton
          text={READINESS_URL}
          label="Copy link"
          style={{ padding: '10px 20px', fontSize: 14 }}
        />
      </div>

      {/* The message */}
      <div style={{
        backgroundColor: 'white', border: `1px solid ${COLORS.rule}`,
        borderRadius: 14, padding: 24,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
          fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: 0.6, color: COLORS.ink3,
        }}>
          <MessageSquare size={14} color={COLORS.brand} /> Message you can send as-is
        </div>
        <p style={{
          fontSize: 15, color: COLORS.ink2, lineHeight: 1.65,
          backgroundColor: COLORS.bg, border: `1px dashed ${COLORS.rule}`,
          borderRadius: 10, padding: '16px 18px', margin: '0 0 16px',
        }}>
          {SHARE_MESSAGE}
        </p>
        <CopyButton
          text={SHARE_MESSAGE}
          label="Copy message"
          style={{ padding: '12px 24px', fontSize: 14 }}
        />
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// Coming soon — referral tracking teaser
// ─────────────────────────────────────────────────────────────
const ComingSoon = () => (
  <section style={{ padding: '64px 24px', backgroundColor: 'white' }}>
    <div style={{
      maxWidth: 720, margin: '0 auto',
      backgroundColor: COLORS.brandSoft, border: `1px solid ${COLORS.brandBorder}`,
      borderRadius: 18, padding: '36px 32px', textAlign: 'center',
    }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 12px', borderRadius: 999,
        backgroundColor: 'white', color: COLORS.brand,
        fontSize: 12, fontWeight: 700,
        marginBottom: 14, textTransform: 'uppercase', letterSpacing: 0.5,
      }}>
        <Trophy size={12} /> Coming soon
      </div>
      <h3 style={{
        fontSize: 22, fontWeight: 800, color: COLORS.brandDark,
        margin: '0 0 12px', lineHeight: 1.25,
      }}>
        Personal referral links and a top-agent leaderboard.
      </h3>
      <p style={{
        fontSize: 15, color: '#3730a3', lineHeight: 1.6,
        margin: '0 auto', maxWidth: 540,
      }}>
        Soon you'll get your own referral code so every buyer you send is counted — with a
        leaderboard recognizing the agents who send the most prepared buyers. To be clear:
        we'll never pay for referrals. No payouts, no disclosures, no strings — just proof
        that you're the agent who sends buyers in ready.
      </p>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────
// FAQ
// ─────────────────────────────────────────────────────────────
const FAQ = () => {
  const items = [
    {
      q: 'What does it cost my buyer?',
      a: 'Nothing — ever. LoanKnow is completely free for buyers: no credit pull, no card, no commitment, no fees. Lenders pay to compete for prepared borrowers, so your client never pays a dime.',
    },
    {
      q: 'Will anyone call or email my client?',
      a: 'No. That\'s the whole point. Your buyer\'s information is never sold and nothing is shared with anyone unless the buyer explicitly chooses to. To lenders they\'re an anonymous borrower number until they decide otherwise. We never market, sell, or email anything that didn\'t come from their direction.',
    },
    {
      q: 'Is LoanKnow a lender or a brokerage?',
      a: 'Neither. LoanKnow is a technology platform — we don\'t make credit decisions, originate loans, or employ agents. We prepare buyers, and when they\'re ready, vetted lenders compete for their business. Your buyer\'s agency relationship is untouched.',
    },
    {
      q: 'Do I need to sign up or create an account?',
      a: 'No. Just share the link. Personal referral codes (so your referrals are counted toward the leaderboard) are coming soon — no account, fee, or contract will be required for those either.',
    },
    {
      q: 'Is there a RESPA issue with recommending this?',
      a: 'Recommending LoanKnow involves no payment, fee exchange, or thing of value in either direction — we don\'t pay for referrals and your buyer pays nothing to get their score. There\'s nothing to disclose. (As always, follow your brokerage\'s policies.)',
    },
    {
      q: 'What does my buyer actually walk away with?',
      a: 'A Readiness Score out of 100 built from about a dozen questions, their realistic price range, the loan programs that fit their situation (FHA, VA, conventional, and more), and a concrete list of what would improve their position. They can email themselves a report and pick up where they left off anytime.',
    },
  ];
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section style={{ padding: '96px 24px 64px', backgroundColor: COLORS.bg }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <SectionEyebrow>Common questions</SectionEyebrow>
        <h2 style={{
          fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)', fontWeight: 800,
          lineHeight: 1.15, color: COLORS.ink,
          textAlign: 'center', maxWidth: 600, margin: '8px auto 40px',
        }}>
          What agents ask before they share it.
        </h2>
        <div style={{
          backgroundColor: 'white', borderRadius: 12,
          border: `1px solid ${COLORS.rule}`, overflow: 'hidden',
        }}>
          {items.map((item, i) => {
            const open = openIdx === i;
            return (
              <div key={i} style={{ borderTop: i === 0 ? 'none' : `1px solid ${COLORS.rule}` }}>
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', gap: 16,
                    padding: '20px 24px', background: 'none', border: 'none',
                    fontSize: 16, fontWeight: 600, color: COLORS.ink,
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span>{item.q}</span>
                  {open ? <ChevronUp size={18} color={COLORS.ink3} /> : <ChevronDown size={18} color={COLORS.ink3} />}
                </button>
                {open && (
                  <div style={{
                    padding: '0 24px 20px', fontSize: 15,
                    color: COLORS.ink2, lineHeight: 1.65,
                  }}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Final CTA */}
        <div style={{ textAlign: 'center', marginTop: 48 }}>
          <CopyButton text={READINESS_URL} label="Copy the link to share" />
          <p style={{ fontSize: 13, color: COLORS.ink3, marginTop: 14 }}>
            loanknow.com/readiness — free, anonymous, about 3 minutes
          </p>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const SectionEyebrow = ({ children, color }) => (
  <div style={{
    fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: 1.2,
    color: color || COLORS.brand,
    textAlign: 'center', marginBottom: 4,
  }}>
    {children}
  </div>
);

export default RealtorLandingPage;
